import { normalizeUnit } from './units';

/**
 * Helpers de preparación/serialización de un producto de la carta para el
 * formulario de edición (NER-4486).
 *
 * Contexto del bug: el backend `GET /menu` devuelve `modifierGroups` **poblado**
 * (array de objetos `{ groupId, name, minSelect, maxSelect, required, options }`),
 * no como IDs. El `ItemForm` (checkboxes de "Grupos de Modificadores") espera un
 * array de **IDs string** (`formData.modifierGroups.includes(group.groupId)`), así
 * que al editar un producto con grupos asignados los checkboxes salían todos
 * desmarcados y, al guardar, se persistían objetos → en el siguiente `getMenu` el
 * `modifierGroupsMap[objeto]` da `undefined` y el item quedaba SIN modificadores
 * para el cliente.
 *
 * Estos helpers viven en un módulo aparte (y no inline en la página) porque el
 * mismo bloque de preparación estaba duplicado en `MenuPage.handleOpenEditItem`
 * (path principal, via `location.state`) y en `ItemFormPage.prepareItemForEdit`
 * (fallback al abrir la URL directo). Consolidar acá evita que un path quede sin
 * el fix, y los deja testeables con Jest.
 */

/**
 * Normaliza `modifierGroups` a un array de IDs string únicos.
 * Acepta tanto objetos poblados (`{ groupId }`) como IDs ya-string (drafts viejos
 * en localStorage pueden traer cualquiera de los dos), y descarta valores vacíos.
 */
export function normalizeModifierGroupIds(modifierGroups) {
  if (!Array.isArray(modifierGroups)) return [];
  const ids = modifierGroups
    .map((g) => (typeof g === 'string' ? g : g && g.groupId))
    .filter(Boolean);
  return [...new Set(ids)];
}

/**
 * Prepara un item de la carta (tal como lo devuelve `getMenu`) para el estado
 * del formulario de edición: stringifica el precio, junta tags/allergens y
 * normaliza los grupos de modificadores a IDs.
 */
export function prepareItemForEdit(item, categoryId) {
  return {
    ...item,
    categoryId,
    price: item.price != null ? item.price.toString() : '',
    tags: (item.tags || []).join(', '),
    allergens: (item.allergens || []).join(', '),
    // La receta la devuelve getMenu solo en contexto admin (RES-48). Si falta queda [] y
    // `sameRecipe` evita reenviarla, así una carga incompleta nunca borra la guardada.
    recipe: Array.isArray(item.recipe) ? item.recipe : [],
    // Idem vínculo de reventa (RES-29): `null` = el producto no se vende como insumo tal cual.
    inventoryLink: item.inventoryLink || null,
    // `modifierGroupIds` son los ids tal como están guardados; `modifierGroups`
    // viene poblado y pierde los grupos que getMenu no pudo resolver (QA R3:
    // se guardaba la lista incompleta y el producto perdía sus modificadores).
    modifierGroups: Array.isArray(item.modifierGroupIds)
      ? normalizeModifierGroupIds(item.modifierGroupIds)
      : normalizeModifierGroupIds(item.modifierGroups),
    isFeatured: item.isFeatured || false,
    isDailySpecial: item.isDailySpecial || false,
    isPromotion: item.isPromotion || false,
    originalPrice: item.originalPrice || '',
    promotionType: item.promotionType || '',
    promotionBadge: item.promotionBadge || '',
    highlightColor: item.highlightColor || '',
    promotionValidFrom: item.promotionValidFrom || '',
    promotionValidTo: item.promotionValidTo || '',
    promotionSchedule: item.promotionSchedule || null,
    storyOrder: item.storyOrder || 0,
  };
}

/**
 * Serializa el `formData` del `ItemForm` para el POST/PUT: parsea el precio,
 * separa tags/allergens y garantiza que `modifierGroups` sea un array de IDs
 * string únicos (defensa ante drafts viejos en `localStorage` que puedan traer
 * objetos).
 */
/** `true` si ambas listas de ids contienen exactamente los mismos ids (sin orden). */
export function sameIdSet(a, b) {
  const setA = new Set(normalizeModifierGroupIds(a));
  const setB = new Set(normalizeModifierGroupIds(b));
  if (setA.size !== setB.size) return false;
  for (const id of setA) if (!setB.has(id)) return false;
  return true;
}

/** Líneas comparables de una receta: insumo, cantidad, unidad canónica y si es crítico, sin orden. */
const recipeLines = (recipe) =>
  (Array.isArray(recipe) ? recipe : [])
    .map((line) => ({
      inventoryItemId: line?.inventoryItemId || '',
      quantity: Number(line?.quantity) || 0,
      unit: normalizeUnit(line?.unit) || '',
      isCritical: line?.isCritical === true,
    }))
    .sort((a, b) =>
      `${a.inventoryItemId}|${a.unit}`.localeCompare(`${b.inventoryItemId}|${b.unit}`)
    );

/**
 * Firma de "hay cambios para enviar". `isCritical` (RES-30) entra: si no, tildarlo no contaría
 * como cambio y la página no reenviaría la receta.
 */
const recipeSignature = (recipe) => JSON.stringify(recipeLines(recipe));

/**
 * Firma de "hay que recalcular el costo": insumo, cantidad y unidad. `isCritical` NO entra
 * porque no afecta el costo (RES-65): con la firma única, tildar Crítico invalidaba el costo que
 * ya había calculado el backend con el `avgCost` del local y el número mostrado saltaba al
 * cálculo local, que cae a `item.cost` y, si el insumo no está o su costo es 0, al snapshot viejo
 * `line.cost`.
 */
const recipeCostSignature = (recipe) =>
  JSON.stringify(
    recipeLines(recipe).map((line) => ({
      inventoryItemId: line.inventoryItemId,
      quantity: line.quantity,
      unit: line.unit,
    }))
  );

/** `true` si ambas recetas tienen los mismos ingredientes, cantidades, unidades y críticos (RES-48). */
export function sameRecipe(a, b) {
  return recipeSignature(a) === recipeSignature(b);
}

/** `true` si ambas recetas dan el mismo costo: ignora `isCritical` (RES-65). */
export function sameRecipeCost(a, b) {
  return recipeCostSignature(a) === recipeCostSignature(b);
}

/** Firma comparable de un vínculo de reventa; `''` cuando no hay vínculo. */
const inventoryLinkSignature = (link) =>
  link && link.inventoryItemId
    ? `${link.inventoryItemId}|${Number(link.quantity) || 0}|${normalizeUnit(link.unit) || ''}`
    : '';

/**
 * `true` si ambos vínculos de reventa son equivalentes — incluido "ninguno de los dos existe"
 * (RES-29). Mismo motivo que `sameRecipe`: si la sección no se tocó, la página no reenvía el
 * campo y el backend conserva lo guardado.
 */
export function sameInventoryLink(a, b) {
  return inventoryLinkSignature(a) === inventoryLinkSignature(b);
}

export function transformItemForSave(formData) {
  // `modifierGroupIds` es de solo lectura (lo agrega getMenu); el backend persiste
  // `modifierGroups`. No lo reenviamos para no mandar dos campos redundantes.
  const data = { ...formData };
  delete data.modifierGroupIds;
  return {
    ...data,
    price: parseFloat(formData.price),
    tags:
      typeof formData.tags === 'string'
        ? formData.tags
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t)
        : formData.tags,
    allergens:
      typeof formData.allergens === 'string'
        ? formData.allergens
            .split(',')
            .map((a) => a.trim())
            .filter((a) => a)
        : formData.allergens,
    modifierGroups: normalizeModifierGroupIds(data.modifierGroups),
  };
}
